import { Lock, Key } from 'lucide-react'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import { CreateIdpProviderDto } from '../../../types/types'
import styles from '../SSOConfiguration.module.css'

interface GoogleProviderFormProps {
  providerForm: CreateIdpProviderDto
  onFieldChange: (field: keyof CreateIdpProviderDto, value: string) => void
  onConfigChange: (key: string, value: string) => void
  hasClientSecret?: boolean
}

export default function GoogleProviderForm({
  providerForm,
  onFieldChange,
  onConfigChange,
  hasClientSecret
}: GoogleProviderFormProps) {
  return (
    <div className={styles.providerFormGrid}>
      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          Name <span className={styles.required}>*</span>
        </label>
        <SecureInput
          fieldType="name"
          label=""
          value={providerForm.name}
          onChange={(value) => onFieldChange('name', value)}
          placeholder="e.g. Google Workspace"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          Description
        </label>
        <SecureInput
          fieldType="text"
          label=""
          value={providerForm.description || ''}
          onChange={(value) => onFieldChange('description', value)}
          placeholder="Optional description"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          Client ID <span className={styles.required}>*</span>
        </label>
        <SecureInput
          fieldType="key"
          label=""
          icon={<Key size={16} />}
          value={providerForm.clientId || ''}
          onChange={(value) => onFieldChange('clientId', value)}
          placeholder="Google OAuth2 Client ID"
          className={styles.input}
        />
        <p className={styles.fieldDescription}>OAuth2 Client ID from Google Cloud Console</p>
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          Client Secret <span className={styles.required}>*</span>
        </label>
        <SecureInput
          fieldType="password"
          label=""
          icon={<Lock size={16} />}
          value={providerForm.clientSecret || ''}
          onChange={(value) => onFieldChange('clientSecret', value)}
          placeholder={hasClientSecret ? 'Secret is set — leave blank to keep it' : 'Google OAuth2 Client Secret'}
          className={styles.input}
          skipPasswordValidation
          autoComplete="new-password"
        />
        <p className={styles.fieldDescription}>
          {hasClientSecret
            ? 'A secret is already configured. Leave blank to keep it, or enter a new value to replace it.'
            : 'OAuth2 Client Secret from Google Cloud Console'}
        </p>
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>Issuer URL</label>
        <SecureInput
          fieldType="url"
          label=""
          value={providerForm.issuerUrl || ''}
          onChange={(value) => onFieldChange('issuerUrl', value)}
          placeholder="https://accounts.google.com"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>Authorization URL</label>
        <SecureInput
          fieldType="text"
          label=""
          value={providerForm.authorizationUrl || ''}
          onChange={(value) => onFieldChange('authorizationUrl', value)}
          placeholder="/o/oauth2/v2/auth"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>Token URL</label>
        <SecureInput
          fieldType="text"
          label=""
          value={providerForm.tokenUrl || ''}
          onChange={(value) => onFieldChange('tokenUrl', value)}
          placeholder="/oauth2/v4/token"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>Scopes</label>
        <SecureInput
          fieldType="text"
          label=""
          value={providerForm.config?.scopes || ''}
          onChange={(value) => onConfigChange('scopes', value)}
          placeholder="openid email profile"
          className={styles.input}
          autoComplete="off"
        />
        <p className={styles.fieldDescription}>OAuth2 scopes to request (space-separated)</p>
      </div>
    </div>
  )
}
